import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { LeagueService, TeamService, SeasonService, PredictionService } from '../../services/firebaseService';
import AddSeasonModal from '../../components/modals/AddSeasonModal';
import {
  isDoubleLikeEliminationWeek,
  isSimultaneousDoubleEliminationWeek,
} from '../../utils/predictionUtils';
import { getAppBaseUrl } from '../../utils/appUrlUtils';
import { 
  ArrowLeftIcon,
  TrophyIcon,
  CogIcon,
  PlusIcon,
  ClipboardDocumentIcon,
  PlayIcon,
  PauseIcon,
  CheckIcon,
  CalendarIcon,
  EyeIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

export default function LeagueDetail() {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  
  const [league, setLeague] = useState(null);
  const [currentSeason, setCurrentSeason] = useState(null);
  const [allSeasons, setAllSeasons] = useState([]);
  const [teams, setTeams] = useState([]);
  const [seasonWeeks, setSeasonWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showAddSeasonModal, setShowAddSeasonModal] = useState(false);
  const [weekFilter, setWeekFilter] = useState('all'); // 'all', 'open', 'closed'
  const [submissionData, setSubmissionData] = useState({});
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);

  useEffect(() => {
    const fetchLeagueData = async () => {
      try {
        // Fetch league details
        const leagueData = await LeagueService.getLeague(leagueId);
        setLeague(leagueData);

        // Fetch all seasons for this league and enrich with season names from global seasons collection
        const allSeasonsData = await LeagueService.getLeagueSeasons(leagueId);
        const enrichedSeasons = await Promise.all(
          allSeasonsData.map(async (s) => {
            const globalSeasonId = s.seasonId || s.id;
            const seasonDetail = await SeasonService.getSeason(globalSeasonId);
            const globalStatus = seasonDetail?.status;
            const isComplete =
              globalStatus === 'complete' ||
              globalStatus === 'closed' ||
              (globalStatus == null && s.draftStatus === 'complete');
            return {
              ...s,
              seasonName: seasonDetail?.name || s.seasonId || s.id,
              seasonStatus: isComplete ? 'complete' : 'active'
            };
          })
        );
        setAllSeasons(enrichedSeasons);

        // Find current (active) season
        const activeSeason = enrichedSeasons.find(season => season.isActive);
        setCurrentSeason(activeSeason);

        // Fetch teams for current season
        if (activeSeason) {
          const teamsData = await TeamService.getTeamsForLeagueSeason(leagueId, activeSeason.seasonId);
          setTeams(teamsData);
          
          // Fetch season weeks
          const seasonData = await SeasonService.getSeason(activeSeason.seasonId);
          if (seasonData && seasonData.weeks) {
            setSeasonWeeks(seasonData.weeks);
          }
          
          // Load prediction submission data
          loadSubmissionData(leagueId, activeSeason.seasonId, teamsData, seasonData?.weeks || []);
        }

      } catch (error) {
        console.error('Error fetching league data:', error);
        setError('Failed to load league data');
      } finally {
        setLoading(false);
      }
    };

    fetchLeagueData();
  }, [leagueId]);

  const handleRenewLeague = async () => {
    try {
      await LeagueService.renewLeague(leagueId);
      // Refresh league data
      const updatedLeague = await LeagueService.getLeague(leagueId);
      setLeague(updatedLeague);
      
      const updatedSeason = await LeagueService.getCurrentLeagueSeason(leagueId);
      if (updatedSeason) {
        const seasonDetail = await SeasonService.getSeason(updatedSeason.seasonId);
        setCurrentSeason({ ...updatedSeason, seasonName: seasonDetail?.name || updatedSeason.seasonId });
      } else {
        setCurrentSeason(null);
      }
      
      // Refresh teams data
      if (updatedSeason) {
        const teamsData = await TeamService.getTeamsForLeagueSeason(leagueId, updatedSeason.seasonId);
        setTeams(teamsData);
      }
    } catch (error) {
      console.error('Error renewing league:', error);
      setError('Failed to renew league');
    }
  };

  const copyInvitationCode = () => {
    if (currentSeason?.invitationCode) {
      navigator.clipboard.writeText(currentSeason.invitationCode);
      toast.success('Invitation code copied to clipboard!');
    }
  };

  const copyTeamClaimingLink = () => {
    if (currentSeason) {
      const claimingLink = `${getAppBaseUrl()}/player/claim-team/${leagueId}/${currentSeason.seasonId}`;
      navigator.clipboard.writeText(claimingLink);
      toast.success('Team claiming link copied to clipboard!');
    }
  };

  const copyPredictionLink = () => {
    if (currentSeason) {
      const predictionLink = `${getAppBaseUrl()}/predictions2/${leagueId}/${currentSeason.seasonId}`;
      navigator.clipboard.writeText(predictionLink);
      toast.success('Prediction link copied to clipboard!');
    }
  };

  const handleSeasonAdded = async () => {
    // Refresh all seasons data after a new season is added
    try {
      const allSeasonsData = await LeagueService.getLeagueSeasons(leagueId);
      const enrichedSeasons = await Promise.all(
        allSeasonsData.map(async (s) => {
          const seasonDetail = await SeasonService.getSeason(s.seasonId);
          return { ...s, seasonName: seasonDetail?.name || s.seasonId };
        })
      );
      setAllSeasons(enrichedSeasons);
      
      // Find current (active) season
      const activeSeason = enrichedSeasons.find(season => season.isActive);
      setCurrentSeason(activeSeason);
      
      // Refresh teams data
      if (activeSeason) {
        const teamsData = await TeamService.getTeamsForLeagueSeason(leagueId, activeSeason.seasonId);
        setTeams(teamsData);
        
        // Refresh season weeks and submission data
        const seasonData = await SeasonService.getSeason(activeSeason.seasonId);
        if (seasonData && seasonData.weeks) {
          setSeasonWeeks(seasonData.weeks);
          loadSubmissionData(leagueId, activeSeason.seasonId, teamsData, seasonData.weeks);
        }
      }
    } catch (error) {
      console.error('Error refreshing season data:', error);
    }
  };

  const loadSubmissionData = async (leagueId, seasonId, teamsList, weeksList) => {
    try {
      setLoadingSubmissions(true);
      const totalTeams = teamsList.length;
      if (totalTeams === 0 || weeksList.length === 0) {
        setSubmissionData({});
        return;
      }

      // Get all predictions for this league/season
      const allPredictions = await PredictionService.getAllLeaguePredictions(leagueId, seasonId);
      
      // Group predictions by week
      const predictionsByWeek = {};
      allPredictions.forEach(prediction => {
        const weekNum = prediction.weekNumber;
        if (!predictionsByWeek[weekNum]) {
          predictionsByWeek[weekNum] = [];
        }
        predictionsByWeek[weekNum].push(prediction);
      });

      // Calculate submission stats for each week
      const submissionStats = {};
      weeksList.forEach(week => {
        const weekNum = week.weekNumber;
        const weekPredictions = predictionsByWeek[weekNum] || [];
        
        // Get unique team IDs that have predictions for this week
        const teamsWithPredictions = new Set(weekPredictions.map(p => p.teamId));
        const teamsWithCount = teamsWithPredictions.size;
        const teamsWithoutCount = totalTeams - teamsWithCount;
        const percentage = totalTeams > 0 ? Math.round((teamsWithCount / totalTeams) * 100) : 0;

        submissionStats[weekNum] = {
          totalTeams,
          teamsWithPredictions: teamsWithCount,
          teamsWithoutPredictions: teamsWithoutCount,
          percentage
        };
      });

      setSubmissionData(submissionStats);
    } catch (error) {
      console.error('Error loading submission data:', error);
    } finally {
      setLoadingSubmissions(false);
    }
  };

  // Get progress bar color based on percentage
  const getProgressColor = (percentage) => {
    if (percentage >= 75) return 'bg-green-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Get progress bar text color
  const getProgressTextColor = (percentage) => {
    if (percentage >= 75) return 'text-green-700';
    if (percentage >= 50) return 'text-yellow-700';
    return 'text-red-700';
  };

  // Week status helper functions
  const getWeekStatus = (week) => {
    if (week.results && Object.keys(week.results).length > 0) {
      return { status: 'results', label: 'Results Entered', color: 'bg-gray-600 text-white', showCheckmark: true };
    } else if (week.isOpenForPredictions === true) {
      return { status: 'open', label: 'Open for Predictions', color: 'bg-green-100 text-green-800', showCheckmark: false };
    } else {
      return { status: 'closed', label: 'Closed', color: 'bg-red-100 text-red-800', showCheckmark: false };
    }
  };

  // Filter weeks based on selected filter - MUST be before early returns
  const filteredWeeks = useMemo(() => {
    let weeks = seasonWeeks;
    if (weekFilter === 'all') {
      weeks = seasonWeeks;
    } else if (weekFilter === 'open') {
      weeks = seasonWeeks.filter(week => week.isOpenForPredictions === true);
    } else if (weekFilter === 'closed') {
      weeks = seasonWeeks.filter(week => week.isOpenForPredictions === false && (!week.results || Object.keys(week.results).length === 0));
    }
    // Sort by weekNumber descending (most recent first)
    return [...weeks].sort((a, b) => (b.weekNumber || 0) - (a.weekNumber || 0));
  }, [seasonWeeks, weekFilter]);

  // Calculate week status summary - MUST be before early returns
  const weekStatusSummary = useMemo(() => {
    const openWeeks = seasonWeeks.filter(week => week.isOpenForPredictions === true).length;
    const resultsWeeks = seasonWeeks.filter(week => week.results && Object.keys(week.results).length > 0).length;

    return { openWeeks, resultsWeeks };
  }, [seasonWeeks]);

  // Helper functions (must be after hooks but before early returns)
  const getDraftStatusColor = (status) => {
    switch (status) {
      case 'notStarted': return 'bg-gray-100 text-gray-800';
      case 'inProgress': return 'bg-yellow-100 text-yellow-800';
      case 'complete': return 'bg-green-100 text-green-800';
      case 'finalized': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDraftStatusText = (status) => {
    switch (status) {
      case 'notStarted': return 'Not Started';
      case 'inProgress': return 'In Progress';
      case 'complete': return 'Complete';
      case 'finalized': return 'Finalized';
      default: return 'Unknown';
    }
  };

  // Early return AFTER all hooks
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-survivor-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading league data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!league) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">League not found</h2>
            <p className="text-gray-600 mb-4">
              This league does not exist or is no longer available.
            </p>
            <button
              onClick={() => navigate('/manage-leagues')}
              className="text-survivor-600 hover:text-survivor-700 font-medium"
            >
              Back to leagues
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/manage-leagues')}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{league.name}</h1>
              {league.description && (
                <p className="mt-1 text-gray-600">{league.description}</p>
              )}
            </div>
          </div>

          {location.state?.message && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
              <p className="text-green-800">{location.state.message}</p>
            </div>
          )}

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-800">{error}</p>
            </div>
          )}
        </div>

        {/* Active Season - Prominent */}
        {currentSeason ? (
          <div className="bg-white rounded-lg shadow-lg border-2 border-survivor-200 mb-8">
            <div className="px-6 py-6 border-b border-gray-200 bg-survivor-50">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Active Season</h2>
                  <p className="mt-1 text-lg text-gray-600">{currentSeason.seasonName || currentSeason.seasonId}</p>
                  {teams.length > 0 ? (
                    <button
                      onClick={() => navigate(`/manage-leagues/${leagueId}/teams/${currentSeason.seasonId}`)}
                      className="mt-1 text-sm text-survivor-600 hover:text-survivor-700 font-medium"
                    >
                      {teams.length} teams participating
                    </button>
                  ) : (
                    <p className="mt-1 text-sm text-gray-500">No teams yet</p>
                  )}
                  {currentSeason.invitationCode && (
                    <div className="mt-3 flex items-center flex-wrap gap-2">
                      <span className="text-sm text-gray-600">Invitation Code:</span>
                      <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                        {currentSeason.invitationCode}
                      </code>
                      <button
                        onClick={copyInvitationCode}
                        className="text-gray-400 hover:text-gray-600"
                        title="Copy invitation code"
                      >
                        <ClipboardDocumentIcon className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getDraftStatusColor(currentSeason.draftStatus)}`}>
                    {getDraftStatusText(currentSeason.draftStatus)}
                  </span>
                  <button
                    onClick={() => navigate(`/manage-leagues/${leagueId}/standings/${currentSeason.seasonId}`)}
                    className="inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500 whitespace-nowrap"
                  >
                    <TrophyIcon className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">View Standings</span>
                    <span className="sm:hidden">Standings</span>
                  </button>
                  <button
                    onClick={() => navigate(`/manage-leagues/${leagueId}/predictions?season=${currentSeason.seasonId}`)}
                    className="inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500 whitespace-nowrap"
                  >
                    <EyeIcon className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">View All Predictions</span>
                    <span className="sm:hidden">Predictions</span>
                  </button>
                  <div className="relative group">
                    <button
                      onClick={copyTeamClaimingLink}
                      className="inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500 whitespace-nowrap"
                      title="Click to copy team claiming link"
                    >
                      <ClipboardDocumentIcon className="w-4 h-4 mr-2" />
                      <span className="hidden md:inline">Copy Team Claiming Link</span>
                      <span className="md:hidden">Copy Claim Link</span>
                    </button>
                    <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10 pointer-events-none">
                      {currentSeason && `${getAppBaseUrl()}/player/claim-team/${leagueId}/${currentSeason.seasonId}`}
                    </div>
                  </div>
                  <div className="relative group">
                    <button
                      onClick={copyPredictionLink}
                      className="inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500 whitespace-nowrap"
                      title="Click to copy prediction link"
                    >
                      <ClipboardDocumentIcon className="w-4 h-4 mr-2" />
                      <span className="hidden md:inline">Copy Prediction Link</span>
                      <span className="md:hidden">Copy Pred Link</span>
                    </button>
                    <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10 pointer-events-none">
                      {currentSeason && `${getAppBaseUrl()}/predictions2/${leagueId}/${currentSeason.seasonId}`}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              {/* Week Status Summary */}
              <div className="mb-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-900">Week Status Summary</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{weekStatusSummary.openWeeks}</div>
                    <div className="text-xs text-gray-600 mt-1">Weeks Open for Predictions</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-600">{weekStatusSummary.resultsWeeks}</div>
                    <div className="text-xs text-gray-600 mt-1">Weeks with Results</div>
                  </div>
                </div>
              </div>

              {/* Weekly Summaries */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Weekly Summaries</h3>
                  <div className="flex items-center space-x-2">
                    <FunnelIcon className="w-4 h-4 text-gray-400" />
                    <select
                      value={weekFilter}
                      onChange={(e) => setWeekFilter(e.target.value)}
                      className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-survivor-500"
                    >
                      <option value="all">All Weeks</option>
                      <option value="open">Open for Predictions</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                </div>
                {filteredWeeks.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
                    {filteredWeeks.map((week, index) => {
                      const weekStatus = getWeekStatus(week);
                      return (
                        <button
                          key={index}
                          onClick={() => navigate(`/manage-leagues/${leagueId}/season/${currentSeason.seasonId}/week/${week.weekNumber}`)}
                          className="flex flex-col items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-survivor-300 transition-colors relative"
                          title={weekStatus.label}
                        >
                          <div className="text-sm font-medium text-gray-900">Week {week.weekNumber}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {week.airDate ? (() => {
                              try {
                                const date = week.airDate.toDate ? week.airDate.toDate() : new Date(week.airDate);
                                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                              } catch (error) {
                                return 'TBD';
                              }
                            })() : 'TBD'}
                          </div>
                          <div className="mt-1 flex flex-wrap gap-1 justify-center">
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${weekStatus.color}`}>
                              {weekStatus.status === 'results' && <CheckIcon className="w-3 h-3 mr-0.5 text-green-400" />}
                              {weekStatus.label}
                            </span>
                          </div>
                          <div className="mt-1 flex flex-wrap gap-1 justify-center">
                            {week.isMergeWeek && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                Merge
                              </span>
                            )}
                            {week.isFinale && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                Finale
                              </span>
                            )}
                            {isDoubleLikeEliminationWeek(week) && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                {isSimultaneousDoubleEliminationWeek(week)
                                  ? 'Double (sim.)'
                                  : 'Double'}
                              </span>
                            )}
                            {week.eliminationType === 'triple' && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                Triple
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No weeks match the selected filter.
                  </div>
                )}
              </div>

              {/* Prediction Submission Status */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Prediction Submission Status</h3>
                  <div className="flex items-center space-x-2">
                    <FunnelIcon className="w-4 h-4 text-gray-400" />
                    <select
                      value={weekFilter}
                      onChange={(e) => setWeekFilter(e.target.value)}
                      className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-survivor-500"
                    >
                      <option value="all">All Weeks</option>
                      <option value="open">Open for Predictions</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                </div>
                {loadingSubmissions ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-survivor-600 mx-auto"></div>
                    <p className="mt-2 text-sm text-gray-600">Loading submission data...</p>
                  </div>
                ) : filteredWeeks.length > 0 ? (
                  <div className="space-y-4">
                    {filteredWeeks.map((week, index) => {
                      const weekNum = week.weekNumber;
                      const stats = submissionData[weekNum] || { 
                        totalTeams: teams.length, 
                        teamsWithPredictions: 0, 
                        teamsWithoutPredictions: teams.length, 
                        percentage: 0 
                      };
                      const progressColor = getProgressColor(stats.percentage);
                      const textColor = getProgressTextColor(stats.percentage);
                      
                      return (
                        <div
                          key={index}
                          className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-3">
                              <h4 className="text-sm font-semibold text-gray-900">
                                Week {weekNum}
                              </h4>
                              {week.airDate && (
                                <span className="text-xs text-gray-500">
                                  {(() => {
                                    try {
                                      const date = week.airDate.toDate ? week.airDate.toDate() : new Date(week.airDate);
                                      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                    } catch (error) {
                                      return 'TBD';
                                    }
                                  })()}
                                </span>
                              )}
                              {week.isFinale && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Finale
                                </span>
                              )}
                              {isDoubleLikeEliminationWeek(week) && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                  {isSimultaneousDoubleEliminationWeek(week)
                                    ? 'Double (sim.)'
                                    : 'Double'}
                                </span>
                              )}
                              {week.eliminationType === 'triple' && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                  Triple
                                </span>
                              )}
                            </div>
                            <div className="text-right">
                              <span className={`text-sm font-semibold ${textColor}`}>
                                {stats.percentage}%
                              </span>
                              <span className="text-xs text-gray-500 ml-1">
                                ({stats.teamsWithPredictions}/{stats.totalTeams} teams)
                              </span>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div
                              className={`${progressColor} h-2.5 rounded-full transition-all duration-300`}
                              style={{ width: `${stats.percentage}%` }}
                            ></div>
                          </div>
                          <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
                            <span>
                              {stats.teamsWithPredictions} teams submitted
                            </span>
                            <span>
                              {stats.teamsWithoutPredictions} teams remaining
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No weeks match the selected filter.
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Draft Management */}
                <div className="lg:col-span-2">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Draft Management</h3>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-sm text-gray-600">Current Status</p>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDraftStatusColor(currentSeason.draftStatus)}`}>
                        {getDraftStatusText(currentSeason.draftStatus)}
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      {(currentSeason.draftStatus === 'notStarted' ||
                        currentSeason.draftStatus === 'inProgress' ||
                        currentSeason.draftStatus === 'complete' ||
                        currentSeason.draftStatus === 'finalized') && (
                        <button
                          onClick={() =>
                            navigate(
                              `/manage-leagues/${leagueId}/draft/${currentSeason.seasonId}`
                            )
                          }
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500"
                        >
                          {currentSeason.draftStatus === 'notStarted' ? (
                            <>
                              <PlayIcon className="w-4 h-4 mr-2" />
                              Set Up / Start Draft
                            </>
                          ) : (
                            <>
                              <CogIcon className="w-4 h-4 mr-2" />
                              Manage Draft
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  
                </div>

                {/* Season Settings */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Season Settings</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => navigate(`/manage-leagues/${leagueId}/scoring/${currentSeason.seasonId}`)}
                      className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500"
                    >
                      <TrophyIcon className="w-4 h-4 mr-2" />
                      Scoring Rules
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-8 text-center mb-8">
            <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">No Active Season</h3>
            <p className="mt-1 text-sm text-gray-500">
              Add a season to start managing your league.
            </p>
            <div className="mt-6">
              <button
                onClick={() => setShowAddSeasonModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500"
              >
                <CalendarIcon className="w-4 h-4 mr-2" />
                Add Season
              </button>
            </div>
          </div>
        )}

        {/* All Seasons */}
        {allSeasons.length > 0 && (
          <div className="bg-white rounded-lg shadow mb-8">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">All Seasons</h2>
              <p className="mt-1 text-sm text-gray-600">
                Seasons your league has participated in
              </p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {allSeasons
                  .sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds) // Sort by newest first
                  .map((season) => (
                    <div
                      key={season.id}
                      className={`p-4 rounded-lg border-2 ${
                        season.isActive 
                          ? 'border-survivor-200 bg-survivor-50' 
                          : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium text-gray-900">
                          {season.seasonName || season.seasonId}
                        </h3>
                        {season.seasonStatus === 'complete' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Complete
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-survivor-100 text-survivor-800">
                            Active
                          </span>
                        )}
                        {season.isActive && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Current
                          </span>
                        )}
                      </div>
                      
                      <div className="space-y-2 text-sm text-gray-600">
                        <div className="flex justify-between">
                          <span>Draft Status:</span>
                          <span className={`font-medium ${getDraftStatusColor(season.draftStatus)} px-2 py-1 rounded-full text-xs`}>
                            {getDraftStatusText(season.draftStatus)}
                          </span>
                        </div>
                        
                        {season.invitationCode && (
                          <div className="flex justify-between">
                            <span>Invitation Code:</span>
                            <code className="text-xs font-mono bg-gray-100 px-1 py-0.5 rounded">
                              {season.invitationCode}
                            </code>
                          </div>
                        )}
                        
                        {season.createdAt && (
                          <div className="flex justify-between">
                            <span>Created:</span>
                            <span className="text-xs">
                              {new Date(season.createdAt.seconds * 1000).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          onClick={() => navigate(`/manage-leagues/${leagueId}/standings/${season.seasonId}`)}
                          className="flex-1 min-w-0 text-xs px-3 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                        >
                          Standings
                        </button>
                        <button
                          onClick={() => navigate(`/manage-leagues/${leagueId}/predictions?season=${season.seasonId}`)}
                          className="flex-1 min-w-0 text-xs px-3 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                        >
                          Predictions
                        </button>
                        {season.isActive && (
                          <button
                            onClick={() => navigate(`/manage-leagues/${leagueId}/scoring/${season.seasonId}`)}
                            className="flex-1 min-w-0 text-xs px-3 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                          >
                            Scoring
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Content will be added here as needed */}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* League Info */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">League Info</h3>
              <div className="space-y-3">
                {currentSeason?.invitationCode ? (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Invitation Code:</span>
                    <div className="flex items-center">
                      <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                        {currentSeason.invitationCode}
                      </code>
                      <button
                        onClick={copyInvitationCode}
                        className="ml-2 text-gray-400 hover:text-gray-600"
                      >
                        <ClipboardDocumentIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Invitation Code:</span>
                    <span className="text-sm text-gray-400">No active season</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Max Teams:</span>
                  <span className="text-sm font-medium">{league.maxTeams}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Privacy:</span>
                  <span className="text-sm font-medium">
                    {league.isPrivate ? 'Private' : 'Public'}
                  </span>
                </div>
              </div>
            </div>

            {/* Additional Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={() => setShowAddSeasonModal(true)}
                  className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500"
                >
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  Add Season
                </button>
                <button
                  onClick={handleRenewLeague}
                  className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Renew League
                </button>
                <button
                  onClick={() => navigate('/manage-leagues/create')}
                  className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Create new league
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Season Modal */}
      <AddSeasonModal
        isOpen={showAddSeasonModal}
        onClose={() => setShowAddSeasonModal(false)}
        leagueId={leagueId}
        onSeasonAdded={handleSeasonAdded}
      />
    </div>
  );
}
